import React, { useState, useEffect } from 'react';
import Icon from '../shared/Icon';
import './TranscriptViewerModal.css';
import { apiService } from '../../../services/api';

function formatClock(ms) {
  const total = Math.floor((ms || 0) / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Read-only transcript viewer for a voice-recorded dataset (PRD-941). Transcript is
 * not editable in v1 — re-record is the correction path.
 */
function TranscriptViewerModal({ isOpen, datasetId, datasetName, onClose, onShowNotification }) {
  const [loading, setLoading] = useState(true);
  const [transcript, setTranscript] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !datasetId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setTranscript(null);
    apiService
      .getDatasetTranscript(datasetId)
      .then((data) => {
        if (!cancelled) setTranscript(data);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err?.responseData?.detail || err?.message || 'Failed to load transcript.';
        const safeMsg = typeof msg === 'string' ? msg : 'Failed to load transcript.';
        setError(safeMsg);
        if (onShowNotification) onShowNotification(safeMsg, 'error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, datasetId]);

  if (!isOpen) return null;

  const segments = transcript?.segments || [];
  const stillProcessing = transcript && transcript.status && transcript.status !== 'ready';

  return (
    <div className="transcript-overlay" role="dialog" aria-modal="true" aria-label="Transcript">
      <div className="transcript-modal">
        <div className="transcript-header">
          <h2 className="transcript-title">{datasetName || 'Transcript'}</h2>
          <button type="button" className="transcript-close" onClick={onClose} aria-label="Close">
            <Icon name="close" />
          </button>
        </div>
        <div className="transcript-divider" />

        <div className="transcript-body">
          {loading && <p className="transcript-status">Loading transcript…</p>}
          {!loading && error && <p className="transcript-status transcript-error">{error}</p>}
          {!loading && !error && stillProcessing && (
            <p className="transcript-status">Transcript is still processing. Check back shortly.</p>
          )}
          {!loading && !error && !stillProcessing && segments.length === 0 && transcript?.full_text && (
            <p className="transcript-fulltext">{transcript.full_text}</p>
          )}
          {!loading && !error && !stillProcessing && segments.length === 0 && !transcript?.full_text && (
            <p className="transcript-status">No transcript text available.</p>
          )}
          {!loading && !error && segments.length > 0 && (
            <div className="transcript-segments">
              {segments.map((seg) => (
                <div key={seg.id} className="transcript-segment">
                  <span className="transcript-ts">{formatClock(seg.start_ms)}</span>
                  <span className="transcript-text">{seg.text}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TranscriptViewerModal;
